<script setup>
import { ref, computed, watch, onMounted } from "vue";
import { useStore } from "dashboard/composables/store";
import { useAccount } from "dashboard/composables/useAccount";
import ContactMetricsChart from "./ContactMetricsChart.vue";

const props = defineProps({
  contactId: { type: Number, required: true },
});

const store = useStore();
const { accountId } = useAccount();

const activePeriod = ref("30d");
const PERIODS = ["7d", "15d", "30d", "60d", "90d", "Máx"];

// Store getters
const score = computed(() =>
  store.getters["contactScores/getScore"](props.contactId),
);
const rawEvents = computed(() =>
  store.getters["contactEvents/getEvents"](props.contactId),
);

// Aggregate raw events into per-day buckets for the chart
const engagementData = computed(() => {
  const counts = {};
  rawEvents.value.forEach((ev) => {
    const date = (ev.occurred_at || "").substring(0, 10);
    if (date) counts[date] = (counts[date] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([date, count]) => ({ date, count }))
    .sort((a, b) => a.date.localeCompare(b.date));
});

const chartData = computed(() => {
  const periodDays = {
    "7d": 7,
    "15d": 15,
    "30d": 30,
    "60d": 60,
    "90d": 90,
    Máx: 9999,
  };
  const days = periodDays[activePeriod.value] || 30;
  return engagementData.value.slice(-days);
});

const chartTotalEvents = computed(() =>
  chartData.value.reduce((s, d) => s + d.count, 0),
);

// KPI counts computed from raw event list
const cutoff30d = computed(() =>
  new Date(Date.now() - 30 * 86400000).toISOString(),
);

const kpiEvents30d = computed(
  () => rawEvents.value.filter((e) => e.occurred_at >= cutoff30d.value).length,
);

const kpiSessions = computed(
  () =>
    rawEvents.value.filter(
      (e) =>
        e.event_type === "session_start" && e.occurred_at >= cutoff30d.value,
    ).length,
);

const kpiPurchases = computed(() => {
  const purchases = rawEvents.value.filter((e) => e.event_type === "purchase");
  const total = purchases.reduce(
    (s, e) => s + (e.properties?.total || e.properties?.value || 0),
    0,
  );
  return { count: purchases.length, total };
});

// Unified metrics object — shape identical to mock so template is untouched
const metrics = computed(() => {
  const s = score.value;
  return {
    events_30d: { count: kpiEvents30d.value, delta: 0 },
    sessions: { count: kpiSessions.value, delta: 0 },
    purchases: kpiPurchases.value,
    lead_score: { score: s?.lead_score ?? 0, label: s?.lead_label ?? "frio" },
    rfm: {
      r: s?.rfm_r ?? 0,
      f: s?.rfm_f ?? 0,
      m: s?.rfm_m ?? 0,
      segment: s?.rfm_segment ?? "unscored",
      segment_label: s?.rfm_segment_label ?? "Sem Dados",
      segment_description: s?.rfm_segment_description ?? "",
      ticket_medio: s?.ticket_medio ?? 0,
      total_gasto: s?.total_purchase_value ?? 0,
      frequencia_12m: s?.rfm_frequency ?? 0,
      intervalo_dias: s?.interval_days ?? null,
      ultima_compra_at: s?.last_purchase_at ?? null,
      rfm_composite: s?.rfm_composite_score ?? 0,
      rfm_percentile: s?.rfm_percentile ?? null,
    },
  };
});

const LEAD_LABEL_CONFIG = {
  frio: { class: "text-n-slate-10", dot: "bg-n-slate-8" },
  morno: { class: "text-n-amber-11", dot: "bg-n-amber-9" },
  quente: { class: "text-n-teal-11", dot: "bg-n-teal-9" },
  muito_quente: { class: "text-n-ruby-11", dot: "bg-n-ruby-9" },
};

const leadLabelConfig = computed(
  () =>
    LEAD_LABEL_CONFIG[metrics.value.lead_score.label] || LEAD_LABEL_CONFIG.frio,
);

const RFM_SEGMENT_ICONS = {
  champion: "i-lucide-trophy",
  loyal: "i-lucide-heart",
  potential_loyal: "i-lucide-star",
  new_customer: "i-lucide-sparkles",
  promising: "i-lucide-trending-up",
  needs_attention: "i-lucide-alert-circle",
  at_risk: "i-lucide-alert-triangle",
  cant_lose: "i-lucide-shield",
  hibernating: "i-lucide-moon",
  lost: "i-lucide-x-circle",
  unscored: "i-lucide-circle-dashed",
};

const segmentIcon = computed(
  () =>
    RFM_SEGMENT_ICONS[metrics.value.rfm.segment] || "i-lucide-circle-dashed",
);

const formatCurrency = (val) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    val ?? 0,
  );

const formatDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const months = [
    "jan",
    "fev",
    "mar",
    "abr",
    "mai",
    "jun",
    "jul",
    "ago",
    "set",
    "out",
    "nov",
    "dez",
  ];
  return `${d.getDate()} de ${months[d.getMonth()]}. de ${d.getFullYear()}`;
};

const daysAgo = (iso) => {
  if (!iso) return "—";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days === 0 ? "hoje" : `há ${days} dia${days > 1 ? "s" : ""}`;
};

const loadData = () => {
  store.dispatch("contactScores/fetchScore", {
    accountId: accountId.value,
    contactId: props.contactId,
  });
  store.dispatch("contactEvents/fetchEvents", {
    accountId: accountId.value,
    contactId: props.contactId,
    params: { per_page: 500 },
  });
};

onMounted(loadData);
watch(() => props.contactId, loadData);

const totalGastoText = computed(
  () => `Total: ${formatCurrency(metrics.value.rfm.total_gasto)}`,
);
const frequenciaText = computed(() => `${metrics.value.rfm.frequencia_12m}×`);
const intervaloDiasText = computed(
  () => `a cada ~${metrics.value.rfm.intervalo_dias} dias`,
);
const rfmScoreText = computed(
  () =>
    `${metrics.value.rfm.r}-${metrics.value.rfm.f}-${metrics.value.rfm.m} · top ${metrics.value.rfm.rfm_percentile}%`,
);
</script>

<template>
  <div class="flex flex-col gap-4 p-4">
    <!-- KPI Cards -->
    <div class="grid grid-cols-2 gap-3">
      <!-- Eventos 30d -->
      <div
        class="m-0.5 px-5 py-4 rounded-xl shadow outline outline-1 outline-n-container bg-n-solid-2 flex flex-col gap-1"
      >
        <span
          class="text-xs font-medium text-n-slate-10 uppercase tracking-wider"
        >
          {{ $t("CONTACT_METRICS.KPI.EVENTS_30D") }}
        </span>
        <div class="flex items-end justify-between mt-1">
          <span class="text-2xl font-semibold text-n-slate-12 leading-none">
            {{ metrics.events_30d.count }}
          </span>
          <span class="text-xs font-medium text-n-teal-11 mb-0.5">
            +{{ metrics.events_30d.delta }}%
          </span>
        </div>
      </div>

      <!-- Sessões -->
      <div
        class="m-0.5 px-5 py-4 rounded-xl shadow outline outline-1 outline-n-container bg-n-solid-2 flex flex-col gap-1"
      >
        <span
          class="text-xs font-medium text-n-slate-10 uppercase tracking-wider"
        >
          {{ $t("CONTACT_METRICS.KPI.SESSIONS") }}
        </span>
        <div class="flex items-end justify-between mt-1">
          <span class="text-2xl font-semibold text-n-slate-12 leading-none">
            {{ metrics.sessions.count }}
          </span>
          <span class="text-xs font-medium text-n-teal-11 mb-0.5">
            +{{ metrics.sessions.delta }}%
          </span>
        </div>
      </div>

      <!-- Compras -->
      <div
        class="m-0.5 px-5 py-4 rounded-xl shadow outline outline-1 outline-n-container bg-n-solid-2 flex flex-col gap-1"
      >
        <span
          class="text-xs font-medium text-n-slate-10 uppercase tracking-wider"
        >
          {{ $t("CONTACT_METRICS.KPI.PURCHASES") }}
        </span>
        <div class="flex items-end justify-between mt-1">
          <span class="text-2xl font-semibold text-n-slate-12 leading-none">
            {{ metrics.purchases.count }}
          </span>
          <span class="text-xs font-medium text-n-teal-11 mb-0.5">
            {{ formatCurrency(metrics.purchases.total) }}
          </span>
        </div>
      </div>

      <!-- Score Lead -->
      <div
        class="m-0.5 px-5 py-4 rounded-xl shadow outline outline-1 outline-n-container bg-n-solid-2 flex flex-col gap-1"
      >
        <span
          class="text-xs font-medium text-n-slate-10 uppercase tracking-wider"
        >
          {{ $t("CONTACT_METRICS.KPI.LEAD_SCORE") }}
        </span>
        <div class="flex items-end justify-between mt-1">
          <span class="text-2xl font-semibold text-n-slate-12 leading-none">
            {{ metrics.lead_score.score }}
          </span>
          <span
            class="inline-flex items-center gap-1 text-xs font-medium mb-0.5"
            :class="leadLabelConfig.class"
          >
            <span class="size-1.5 rounded-full" :class="leadLabelConfig.dot" />
            {{ metrics.lead_score.label }}
          </span>
        </div>
      </div>
    </div>

    <!-- RFM Analysis -->
    <div
      class="m-0.5 px-5 py-4 rounded-xl shadow outline outline-1 outline-n-container bg-n-solid-2"
    >
      <!-- Section header -->
      <div class="flex items-center gap-2 mb-4">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t("CONTACT_METRICS.RFM.TITLE") }}
        </span>
        <div class="flex items-center gap-1.5">
          <span
            class="text-xs text-n-slate-10 px-1.5 py-0.5 rounded bg-n-solid-3 border border-n-weak"
          >
            {{ $t("CONTACT_METRICS.RFM.RECENCY") }}
          </span>
          <span class="size-1 rounded-full bg-n-slate-10" />
          <span
            class="text-xs text-n-slate-10 px-1.5 py-0.5 rounded bg-n-solid-3 border border-n-weak"
          >
            {{ $t("CONTACT_METRICS.RFM.FREQUENCY") }}
          </span>
          <span class="size-1 rounded-full bg-n-slate-10" />
          <span
            class="text-xs text-n-slate-10 px-1.5 py-0.5 rounded bg-n-solid-3 border border-n-weak"
          >
            {{ $t("CONTACT_METRICS.RFM.MONETARY") }}
          </span>
        </div>
        <button class="ml-auto">
          <span
            class="i-lucide-info size-4 text-n-slate-10 hover:text-n-slate-11 transition-colors"
          />
        </button>
      </div>

      <div class="flex flex-col gap-3">
        <!-- Left: Segment -->
        <div class="bg-n-solid-3 rounded-xl p-4 flex flex-col gap-3">
          <span
            class="text-xs font-medium text-n-slate-10 uppercase tracking-wider"
          >
            {{ $t("CONTACT_METRICS.RFM.SEGMENT") }}
          </span>
          <div class="flex items-center gap-2">
            <span
              class="flex items-center justify-center w-8 h-8 rounded-lg bg-n-teal-3"
            >
              <span class="size-4 text-n-teal-11" :class="segmentIcon" />
            </span>
            <span class="text-lg font-semibold text-n-teal-11">
              {{ metrics.rfm.segment_label }}
            </span>
          </div>
          <p class="text-xs text-n-slate-10 leading-relaxed">
            {{ metrics.rfm.segment_description }}
          </p>
          <!-- R / F / M score dots -->
          <div class="grid grid-cols-3 gap-2">
            <div
              v-for="(dim, key) in {
                R: metrics.rfm.r,
                F: metrics.rfm.f,
                M: metrics.rfm.m,
              }"
              :key="key"
              class="flex flex-col items-center gap-1.5 p-2.5 rounded-lg bg-n-solid-2 outline outline-1 outline-n-container"
            >
              <span class="text-xs font-medium text-n-slate-10 uppercase">{{
                key
              }}</span>
              <div class="flex items-center gap-0.5">
                <span
                  v-for="i in 5"
                  :key="i"
                  class="size-2 rounded-full transition-colors"
                  :class="i <= dim ? 'bg-n-teal-9' : 'bg-n-alpha-2'"
                />
              </div>
              <span
                class="text-base font-semibold text-n-slate-12 leading-none"
                >{{ dim }}</span
              >
            </div>
          </div>
        </div>

        <!-- Right: Stats grid -->
        <div class="grid grid-cols-2 gap-3">
          <!-- Ticket Médio -->
          <div class="bg-n-solid-3 rounded-xl p-4 flex flex-col gap-1">
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-banknote size-3.5 text-n-slate-10" />
              <span
                class="text-xs font-medium text-n-slate-10 uppercase tracking-wider"
              >
                {{ $t("CONTACT_METRICS.RFM.TICKET_MEDIO") }}
              </span>
            </div>
            <span
              class="text-xl font-semibold text-n-slate-12 mt-1 leading-none"
            >
              {{ formatCurrency(metrics.rfm.ticket_medio) }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ totalGastoText }}
            </span>
          </div>

          <!-- Última Compra -->
          <div class="bg-n-solid-3 rounded-xl p-4 flex flex-col gap-1">
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-clock size-3.5 text-n-slate-10" />
              <span
                class="text-xs font-medium text-n-slate-10 uppercase tracking-wider"
              >
                {{ $t("CONTACT_METRICS.RFM.LAST_PURCHASE") }}
              </span>
            </div>
            <span
              class="text-xl font-semibold text-n-slate-12 mt-1 leading-none"
            >
              {{ formatDate(metrics.rfm.ultima_compra_at) }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ daysAgo(metrics.rfm.ultima_compra_at) }}
            </span>
          </div>

          <!-- Frequência 12M -->
          <div class="bg-n-solid-3 rounded-xl p-4 flex flex-col gap-1">
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-refresh-cw size-3.5 text-n-slate-10" />
              <span
                class="text-xs font-medium text-n-slate-10 uppercase tracking-wider"
              >
                {{ $t("CONTACT_METRICS.RFM.FREQUENCY_12M") }}
              </span>
            </div>
            <span
              class="text-xl font-semibold text-n-slate-12 mt-1 leading-none"
            >
              {{ frequenciaText }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ intervaloDiasText }}
            </span>
          </div>

          <!-- RFM Score -->
          <div class="bg-n-solid-3 rounded-xl p-4 flex flex-col gap-1">
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-award size-3.5 text-n-slate-10" />
              <span
                class="text-xs font-medium text-n-slate-10 uppercase tracking-wider"
              >
                {{ $t("CONTACT_METRICS.RFM.RFM_SCORE") }}
              </span>
            </div>
            <span
              class="text-xl font-semibold text-n-blue-11 mt-1 leading-none"
            >
              {{ metrics.rfm.rfm_composite }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ rfmScoreText }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Engagement Chart -->
    <div
      class="m-0.5 px-5 py-4 rounded-xl shadow outline outline-1 outline-n-container bg-n-solid-2"
    >
      <div class="flex flex-col gap-3 mb-4">
        <div class="flex items-center gap-2">
          <span
            class="flex items-center justify-center w-7 h-7 rounded-lg bg-n-teal-3 flex-shrink-0"
          >
            <span class="i-lucide-trending-up size-3.5 text-n-teal-11" />
          </span>
          <div class="flex-1 min-w-0">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t("CONTACT_METRICS.ENGAGEMENT.TITLE") }}
            </span>
            <p class="text-xs text-n-slate-10 mt-0.5">
              {{ chartTotalEvents }}
              {{ $t("CONTACT_METRICS.ENGAGEMENT.EVENTS_IN_PERIOD") }}
            </p>
          </div>
        </div>
        <!-- Period filters -->
        <div
          class="flex flex-wrap items-center gap-0.5 bg-n-solid-3 rounded-lg p-0.5 outline outline-1 outline-n-container w-fit"
        >
          <button
            v-for="period in PERIODS"
            :key="period"
            class="px-2.5 py-1 rounded-md text-xs font-medium transition-colors"
            :class="
              activePeriod === period
                ? 'bg-n-solid-2 text-n-slate-12 shadow-sm outline outline-1 outline-n-container'
                : 'text-n-slate-10 hover:text-n-slate-11'
            "
            @click="activePeriod = period"
          >
            {{ period }}
          </button>
        </div>
      </div>
      <ContactMetricsChart :data="chartData" :height="160" />
    </div>
  </div>
</template>
